import { appPath, listingPath, serverPath, userPath, projectPath, productPath, notificationPath } from './ApiPaths';
import {
  getJson,
  postJson,
  patchJson,
  putJson,
  postFormData,
  putFormData,
  deleteJson,
  deleteJsonQuery,
  deleteNoBody,
  deleteExpect204,
  requestVoid,
} from './services/httpClient';
import {
  IApp,
  IAppLicense,
  IAppSubscription,
  IAppPublic,
  IAppSubscriptionPublicInfo,
} from './types/App.model';
import { IListing, IPopulatedListing } from './types/Listing.model';
import { IUser } from './types/User';
import { IUserPermissions } from './types/UserPermission';
import { IProject } from './types/Project.model';
import { UnifiedAppAPI } from './services/UnifiedAppAPI';
import { IProduct, ProductLockedResponse, type LinkShareMode, type ShareLinkDuration, type ProductShareTokenMeta, type CreateProductShareTokenResponse } from './types/Product.model';

// ============================================================================
// SERVER / UTILITY FUNCTIONS
// ============================================================================

export async function pingServer(): Promise<string> {
  const data = await getJson<string>(serverPath + 'ping');
  return typeof data === 'string' ? data : String(data);
}

/** Change password for the logged-in user (requires session). */
export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<void> {
  await requestVoid(serverPath + 'auth/change-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

/** SuperTokens header tokens for Unity WebGL (reuse platform cookie session). */
export type UnityHandoffTokens = {
  userId: string;
  tokens: {
    'st-access-token': string;
    'front-token'?: string;
  };
};

export async function getUnityHandoffTokens(): Promise<UnityHandoffTokens> {
  return getJson<UnityHandoffTokens>(serverPath + 'auth/unity-handoff');
}

// ============================================================================
// LISTING FUNCTIONS
// ============================================================================

export async function getListingsByAppId(appId: string): Promise<IPopulatedListing[]> {
  return getJson<IPopulatedListing[]>(listingPath + 'byApp/' + appId);
}

export async function getListingsByProductId(productId: string): Promise<IPopulatedListing[]> {
  return getJson<IPopulatedListing[]>(listingPath + 'byProduct/' + productId);
}

/**
 * Update the confirmation status of a listing
 * @param listingId The ID of the listing to update
 * @param userId The ID of the user updating the listing
 * @param isApp Whether the update is for the app confirmation
 * @param isConfirmed The new confirmation status
 * @returns The updated listing
 */
export async function updateListingConfirmation(
  listingId: string,
  userId: string,
  isApp: boolean,
  isConfirmed: boolean
): Promise<IListing | null> {
  return patchJson<IListing | null>(listingPath + listingId + '/confirm', {
    listingId,
    userId,
    isApp,
    isConfirmed,
  });
}

/** Permanently remove a listing (app admin or product owner). */
export async function deleteListing(listingId: string): Promise<void> {
  await deleteExpect204(listingPath + listingId);
}

export async function createListing(
  productId: string,
  appId: string,
  userId: string
): Promise<IListing> {
  return postJson<IListing>(listingPath, { productId, appId, userId });
}

// ============================================================================
// PRODUCT FUNCTIONS
// ============================================================================

/** Response shape from GET /product/filter-options/tags */
export interface TagsForFilterResponse {
  suggested: string[];
  distinct: string[];
  tagCounts?: Record<string, number>;
  /** Tags the logged-in user has used on their own products (create/edit suggestions only). */
  userTags?: string[];
}

export interface CategoriesForFilterResponse {
  suggested: string[];
  distinct: string[];
  categoryCounts?: Record<string, number>;
}

/** Optional filters for product list */
export interface ProductListFilters {
  companies?: string[];
  tags?: string[];
  categories?: string[];
}

// ✅ MIGRATED - Used in Home.page.tsx
export async function getAllProducts(filters?: ProductListFilters): Promise<IProduct[]> {
  const params = new URLSearchParams();
  if (filters?.tags?.length) params.set('tags', filters.tags.join(','));
  if (filters?.companies?.length) params.set('companies', filters.companies.join(','));
  if (filters?.categories?.length) params.set('categories', filters.categories.join(','));
  const query = params.toString();
  const url = query ? `${productPath}?${query}` : productPath;
  return getJson<IProduct[]>(url);
}

export async function getTagsForFilter(): Promise<TagsForFilterResponse> {
  return getJson<TagsForFilterResponse>(`${productPath}filter-options/tags`);
}

export async function getCategoriesForFilter(): Promise<CategoriesForFilterResponse> {
  return getJson<CategoriesForFilterResponse>(`${productPath}filter-options/categories`);
}

/** Get count of products matching filters (for "X results" in filter dropdown). */
export async function getProductListCount(filters?: ProductListFilters): Promise<number> {
  const params = new URLSearchParams();
  if (filters?.tags?.length) params.set('tags', filters.tags.join(','));
  if (filters?.companies?.length) params.set('companies', filters.companies.join(','));
  if (filters?.categories?.length) params.set('categories', filters.categories.join(','));
  const query = params.toString();
  const url = query ? `${productPath}list/count?${query}` : `${productPath}list/count`;
  const body = await getJson<{ count: number }>(url);
  return body.count;
}

export type RelatedProductsQuery = {
  excludeId: string;
  company: string;
  tags: string[];
  category?: string | null;
  limit?: number;
};

/** Product-page related strip — server-capped (default 4), not a full marketplace list. */
export async function getRelatedProducts(
  query: RelatedProductsQuery
): Promise<IProduct[]> {
  const params = new URLSearchParams();
  params.set('excludeId', query.excludeId);
  params.set('company', query.company);
  if (query.tags.length) params.set('tags', query.tags.join(','));
  if (query.category?.trim()) params.set('category', query.category.trim());
  if (query.limit != null) params.set('limit', String(query.limit));
  return getJson<IProduct[]>(`${productPath}related?${params.toString()}`);
}

export function isProductLockedResponse(
  data: unknown
): data is ProductLockedResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'locked' in data &&
    (data as ProductLockedResponse).locked === true
  );
}

// ✅ MIGRATED - Used in EditProduct, ProductViewer, CreateProduct
export async function getProductById(
  productId: string,
  options?: { share?: string | null }
): Promise<IProduct | ProductLockedResponse> {
  const params = new URLSearchParams();
  if (options?.share) params.set('share', options.share);
  const query = params.toString();
  const url = query
    ? `${productPath}${productId}?${query}`
    : productPath + productId;
  return getJson<IProduct | ProductLockedResponse>(url);
}

/** Append `?share=` / `&share=` for model file requests. */
export function withProductShareQuery(
  url: string,
  share: string | null | undefined
): string {
  if (!share) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}share=${encodeURIComponent(share)}`;
}

/** Owner-only: create a temporary share link token (raw token returned once). */
export async function createProductShareToken(
  productId: string,
  duration: ShareLinkDuration
): Promise<CreateProductShareTokenResponse> {
  return postJson<CreateProductShareTokenResponse>(
    `${productPath}${productId}/link-share/tokens`,
    { duration }
  );
}

/** Owner-only: list share token metadata (no hashes / raw tokens). */
export async function listProductShareTokens(
  productId: string
): Promise<ProductShareTokenMeta[]> {
  const body = await getJson<{ tokens: ProductShareTokenMeta[] }>(
    `${productPath}${productId}/link-share/tokens`
  );
  return body.tokens ?? [];
}

/** Owner-only: revoke a temporary share token. */
export async function revokeProductShareToken(
  productId: string,
  tokenId: string
): Promise<{ ok: boolean }> {
  return deleteJson<{ ok: boolean }>(
    `${productPath}${productId}/link-share/tokens/${tokenId}`
  );
}

/** Owner-only product load for create/edit pages (requires session). */
export async function getProductForEdit(productId: string): Promise<IProduct> {
  return getJson<IProduct>(`${productPath}${productId}/for-edit`);
}

/** Set httpOnly cookie for password-protected product link access (same-origin credentials). */
export async function unlockProductLinkShare(
  productId: string,
  password: string
): Promise<{ ok: boolean }> {
  return postJson<{ ok: boolean }>(
    `${productPath}${productId}/link-share/unlock`,
    { password }
  );
}

/** Owner-only: update link share mode and optional password (no full product PUT). */
export async function updateProductLinkShare(
  productId: string,
  payload: { linkShareMode: LinkShareMode; linkSharePassword?: string }
): Promise<IProduct> {
  return patchJson<IProduct>(`${productPath}${productId}/link-share`, payload);
}

export async function starProduct(productId: string): Promise<{ starCount: number }> {
  return postJson<{ starCount: number }>(`${productPath}${productId}/stars`, {});
}

export async function unstarProduct(productId: string): Promise<{ starCount: number }> {
  return deleteJson<{ starCount: number }>(`${productPath}${productId}/stars`);
}

export type AppNotificationType =
  | 'product_created'
  | 'listing_approved'
  | 'share_expired'
  | 'star_milestone'
  | 'views_trending'
  | 'product_trashed'
  | 'product_trash_expiring';

export type AppNotificationPayload = {
  productId?: string;
  productName?: string;
  appName?: string;
  count?: number;
};

export type AppNotification = {
  _id: string;
  userId: string;
  type: AppNotificationType;
  payload: AppNotificationPayload;
  href?: string;
  readAt?: string | null;
  createdAt: string;
  updatedAt?: string;
};

export async function getNotifications(limit = 40): Promise<AppNotification[]> {
  const body = await getJson<{ items: AppNotification[] }>(
    `${notificationPath}?limit=${encodeURIComponent(String(limit))}`
  );
  return body.items ?? [];
}

export async function getNotificationUnreadCount(): Promise<number> {
  const body = await getJson<{ count: number }>(`${notificationPath}unread-count`);
  return body.count ?? 0;
}

export async function markNotificationRead(notificationId: string): Promise<AppNotification> {
  return patchJson<AppNotification>(`${notificationPath}${notificationId}/read`, {});
}

export async function markAllNotificationsRead(): Promise<{ modifiedCount: number }> {
  return patchJson<{ modifiedCount: number }>(`${notificationPath}read-all`, {});
}

/** Public profile: products this user has starred (server returns populated list). */
export async function getStarredProductsByUserId(userId: string): Promise<IProduct[]> {
  return getJson<IProduct[]>(`${userPath}${userId}/starred-products`);
}

// Used in DashBoard (session = full list), UserViewer (no session = public products only)
export async function getProductsByUser(userId: string): Promise<IProduct[]> {
  return getJson<IProduct[]>(productPath + 'byUser/' + userId);
}

// ✅ MIGRATED - Used in EditProduct, CreateProduct, useProductSubmission
export async function createProduct(formData: FormData): Promise<IProduct> {
  return postFormData<IProduct>(productPath, formData);
}

// ✅ MIGRATED - Used in EditProduct, useProductSubmission
export async function updateProduct(formData: FormData): Promise<IProduct> {
  return putFormData<IProduct>(productPath, formData);
}

// ✅ MIGRATED - Used in DashBoard.tsx
export async function deleteProduct(productId: string): Promise<void> {
  await deleteNoBody(productPath + productId);
}

/**
 * Soft-deleted products for this user (trash, ~7 day retention).
 * Uses the same user id as getProductsByUser; session must match userId.
 */
export async function getDeletedProducts(userId: string): Promise<IProduct[]> {
  return getJson<IProduct[]>(`${productPath}byUser/${userId}/deleted`);
}

export async function restoreDeletedProduct(productId: string): Promise<IProduct> {
  return postJson<IProduct>(`${productPath}deleted/${productId}/restore`, {});
}

export async function permanentDeleteDeletedProduct(productId: string): Promise<void> {
  await deleteNoBody(`${productPath}deleted/${productId}`);
}

// ============================================================================
// PROJECT FUNCTIONS
// ============================================================================

export async function getProjectsByUser(userId: string): Promise<IProject[]> {
  return getJson<IProject[]>(projectPath + 'byUser/' + userId);
}

export async function getProject(projectId: string): Promise<IProject> {
  return getJson<IProject>(projectPath + projectId);
}

export async function createProject(name: string, saveString: string): Promise<IProject> {
  return postJson<IProject>(projectPath, { name, saveString });
}

export async function updateProject(
  projectId: string,
  name?: string,
  saveString?: string
): Promise<IProject> {
  return putJson<IProject>(projectPath + projectId, { name, saveString });
}

export async function deleteProject(projectId: string): Promise<void> {
  await deleteNoBody(projectPath + projectId);
}

// Project Member Management

export async function addProjectMember(
  projectId: string,
  email: string,
  role: 'Viewer' | 'Editor'
): Promise<IProject> {
  return postJson<IProject>(projectPath + projectId + '/members', {
    email,
    role,
  });
}

export async function removeProjectMember(projectId: string, id: string): Promise<IProject> {
  return deleteJsonQuery<IProject>(projectPath + projectId + '/members', { id });
}

export async function updateProjectMember(
  projectId: string,
  email: string,
  role: 'Viewer' | 'Editor'
): Promise<IProject> {
  return putJson<IProject>(projectPath + projectId + '/members', { email, role });
}

// ============================================================================
// APP FUNCTIONS
// ============================================================================

// Public & Query Functions

export async function getAppsPublic(): Promise<IAppPublic[]> {
  return getJson<IAppPublic[]>(appPath + 'apps/public');
}

export async function getListableApps(): Promise<IAppPublic[]> {
  return getJson<IAppPublic[]>(appPath + 'apps/listable');
}

export async function getAllApps(): Promise<IApp[]> {
  return getJson<IApp[]>(appPath + 'apps/all');
}

export async function getAllAppsWithAdminEmails(): Promise<any[]> {
  return getJson<any[]>(appPath + 'all/apps/with-admin-emails');
}

interface GetAppsByUserResponse {
  apps: IApp[];
  syncStatus: {
    isOutOfSync: boolean;
    currentAppIdsCount: number;
    expectedAppIdsCount: number;
    error?: string;
  };
}

export async function getAppsByUser(userId: string): Promise<IApp[]> {
  const data = await getJson<GetAppsByUserResponse>(appPath + 'apps/byUser/' + userId);

  // Log sync status if out of sync (critical for monitoring)
  if (data.syncStatus?.isOutOfSync) {
    console.warn('User app_ids is out of sync:', {
      userId,
      currentCount: data.syncStatus.currentAppIdsCount,
      expectedCount: data.syncStatus.expectedAppIdsCount,
      error: data.syncStatus.error,
    });
  }

  return data.apps;
}

// App CRUD

export async function createApp(appName: string): Promise<IApp> {
  return postJson<IApp>(appPath + 'apps', { appName });
}

export async function deleteApp(appId: string): Promise<void> {
  await deleteNoBody(appPath + 'apps/' + appId);
}

// App Licenses

export async function getAppLicensesByAppId(appId: string): Promise<IAppLicense[]> {
  return getJson<IAppLicense[]>(appPath + 'licenses/byApp/' + appId);
}

export async function getAllAppLicenses(): Promise<IAppLicense[]> {
  return getJson<IAppLicense[]>(appPath + 'all/licenses');
}

export async function createLicense(
  appId: string,
  licenseName: string,
  additionalData?: Partial<Omit<IAppLicense, '_id' | 'app_id' | 'licenseName'>>
): Promise<IAppLicense> {
  return postJson<IAppLicense>(appPath + 'licenses', {
    app_id: appId,
    licenseName,
    ...additionalData,
  });
}

export async function updateLicense(
  licenseId: string,
  updateData: Partial<IAppLicense>
): Promise<IAppLicense> {
  return putJson<IAppLicense>(appPath + 'licenses/' + licenseId, updateData);
}

export async function deleteLicense(licenseId: string): Promise<void> {
  await deleteNoBody(appPath + 'licenses/' + licenseId);
}

// App Subscriptions

export async function getAppSubscriptionsByAppId(appId: string): Promise<IAppSubscription[]> {
  return getJson<IAppSubscription[]>(appPath + 'subscriptions/byApp/' + appId);
}

export async function getAllAppSubscriptions(): Promise<IAppSubscription[]> {
  return getJson<IAppSubscription[]>(appPath + 'all/subscriptions');
}

export interface CreateAppSubscriptionData extends Omit<IAppSubscription, '_id'> {}
export async function postAppSubscription(
  subscription: CreateAppSubscriptionData
): Promise<IAppSubscription> {
  return postJson<IAppSubscription>(appPath + 'subscriptions', subscription);
}

export async function patchAppSubscription(
  subscription: IAppSubscription
): Promise<IAppSubscription> {
  return patchJson<IAppSubscription>(appPath + 'subscriptions', subscription);
}

export async function deleteAppSubscription(subscription: IAppSubscription): Promise<boolean> {
  return deleteExpect204(appPath + 'subscriptions/' + subscription._id);
}

// Admin Functions

export async function getAllAdmins(): Promise<any[]> {
  return getJson<any[]>(appPath + 'admins');
}

export async function getAllAdminsWithApps(): Promise<any[]> {
  return getJson<any[]>(appPath + 'admins/with-apps');
}

export async function addAdmin(email: string, appId?: string): Promise<any> {
  return postJson<any>(appPath + 'admins', { email, appId });
}

export async function updateAdminApps(adminId: string, appIds: string[]): Promise<any> {
  return putJson<any>(appPath + 'admins/' + adminId + '/apps', { appIds });
}

export async function removeAdmin(email: string): Promise<any> {
  return deleteJson<any>(appPath + 'admins', { email });
}

// ============================================================================
// UNIFIED APP API - RECOMMENDED APPROACH
// ============================================================================

// LEGACY APP UPDATE FUNCTIONS REMOVED
// All deprecated functions have been removed. Use UnifiedAppAPI instead:
// - patchAppMetadata → UnifiedAppAPI.updateMetadata
// - patchAppTwoPartConnectorSelection → UnifiedAppAPI.updateConnector

// Re-export unified API functions for easy access
export {
  UnifiedAppAPI,
  type AppUpdateContext,
  type UnifiedAppUpdateResponse,
} from './services/UnifiedAppAPI';

// Convenience functions that use the unified API
export async function updateAppUnified(
  appId: string,
  updates: Partial<IApp>,
  logoFile?: File
): Promise<{ message: string; app: IApp; context?: string; updatedFields?: string[] }> {
  return UnifiedAppAPI.updateApp(appId, updates, logoFile);
}

export async function updateAppBasicUnified(
  appId: string,
  updates: { appName?: string; summaryDescription?: string },
  logoFile?: File
): Promise<{ message: string; app: IApp }> {
  return UnifiedAppAPI.updateBasic(appId, updates, logoFile);
}

export async function updateAppMetadataUnified(
  appId: string,
  metadata: any
): Promise<{ message: string; app: IApp }> {
  return UnifiedAppAPI.updateMetadata(appId, metadata);
}

export async function updateAppLimitsUnified(
  appId: string,
  limits: { userLimit?: number; modelLimit?: number }
): Promise<{ message: string; app: IApp }> {
  return UnifiedAppAPI.updateLimits(appId, limits);
}

export async function updateAppPlatformsUnified(
  appId: string,
  platforms: string[]
): Promise<{ message: string; app: IApp }> {
  return UnifiedAppAPI.updatePlatforms(appId, platforms);
}

// ============================================================================
// USER FUNCTIONS
// ============================================================================

export async function getAllUsers(): Promise<IUser[]> {
  return getJson<IUser[]>(userPath + 'all/users');
}

export async function getAllUserPermissions(): Promise<IUserPermissions[]> {
  return getJson<IUserPermissions[]>(userPath + 'all/permissions/');
}

export async function updateUserPermissions(
  userId: string,
  permissions: IUserPermissions
): Promise<IUserPermissions> {
  return patchJson<IUserPermissions>(userPath + userId + '/permissions', permissions);
}

/**
 * All subscriptions of the user (expired ones included), each with its license and app populated.
 * Server: getAppSubscriptionInfosByUser. Unity: PortfolioAPI.GetAppSubscriptionInfosByUser.
 */
export async function getAppSubscriptionInfosByUser(userId: string): Promise<IAppSubscriptionPublicInfo[]> {
  return getJson<IAppSubscriptionPublicInfo[]>(`${appPath}subscriptions/byUser/${userId}`);
}

export async function getTotalUserCount(): Promise<number> {
  const body = await getJson<{ count: number }>(userPath + 'total/count');
  return body.count;
}

export type ProductTotals = { products: number; views: number; stars: number };

/** MasterAdmin only. All non-trashed products of any visibility, with summed views and stars. */
export async function getProductTotals(): Promise<ProductTotals> {
  return getJson<ProductTotals>(productPath + 'total/stats');
}

// ============================================================================
// NEWSLETTER FUNCTIONS
// ============================================================================

export async function subscribeToNewsletter(userId: string, email: string): Promise<any> {
  return postJson<any>(`${serverPath}newsletter/${userId}/subscribe`, { email });
}

export async function unsubscribeFromNewsletter(userId: string, email: string): Promise<any> {
  return postJson<any>(`${serverPath}newsletter/${userId}/unsubscribe`, { email });
}

export async function getNewsletterStatus(
  userId: string
): Promise<{ newsletterSubscribed: boolean }> {
  return getJson<{ newsletterSubscribed: boolean }>(`${serverPath}newsletter/${userId}/status`);
}
